class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover', // Kept name for CSS compatibility
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };
    this.timeout;

    // Change: Listen for click instead of mouseenter
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    // If clicking a link inside, let it happen. 
    // If clicking the toggle/summary, toggle the menu.
    if (event.target.closest('a') && !event.target.closest('.submenu-toggle')) return;

    event.stopPropagation(); // Prevent immediate closing from the document listener
    
    if (this.classList.contains(this.classes.active)) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.header = this.header || document.querySelector(this.selectors.header);
    this.removeClasses(); // Close other open menus
    this.addClasses();
    this.resetBackground();
    this.animateMenu();

    // Add global listener to close when clicking outside
    this.onDocumentClick = this.onDocumentClick || this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  addClasses() {
    this.classList.add(this.classes.active);
    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      if (menu !== this) menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);
    const childAnimate = this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);
    clearTimeout(this.timeout);
    document.removeEventListener('click', this.onDocumentClick);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    if(background) background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate = this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.reset();
    this.resetBackground();
    if (this.header) this.header.classList.remove(this.classes.hover);
  }
}
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo',
      toggle: '.submenu-toggle' // Added to find the button to return focus
    };
    this.timeout = [];

    // Click to toggle
    this.addEventListener('click', this.handleClick.bind(this));
    
    // Listen for Escape key
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  handleClick(event) {
    if (event.target.tagName === 'A' && !event.target.classList.contains('submenu-toggle')) return;
    
    event.stopPropagation();

    if (this.classList.contains(this.classes.active)) {
      this.close();
    } else {
      this.open();
    }
  }

  // Handle the Escape key logic
  handleKeyDown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      if (this.classList.contains(this.classes.active)) {
        this.close();
        
        // Accessibility: Focus the toggle button so the user isn't lost
        const toggle = this.querySelector(this.selectors.toggle);
        if (toggle) toggle.focus();
      }
    }
  }

  open() {
    this.header = this.header || document.querySelector(this.selectors.header);
    this.removeClasses();
    this.addClasses();
    this.showSublist();

    // Update ARIA for accessibility
    const toggle = this.querySelector(this.selectors.toggle);
    if (toggle) toggle.setAttribute('aria-expanded', 'true');

    this.onDocumentClick = this.onDocumentClick || this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  showSublist() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    if (background && dropdown) {
      background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight + 'px');
      background.classList.add(this.classes.active);
    }
  }

  addClasses() {
    this.classList.add(this.classes.active);
    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;
    
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      if (menu !== this) menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);
    
    // Reset ARIA
    const toggle = this.querySelector(this.selectors.toggle);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    
    this.timeout.forEach((timeout) => clearTimeout(timeout));
    this.timeout = [];
    document.removeEventListener('click', this.onDocumentClick);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    if(background) background.classList.remove(this.classes.active);
  }

  close() {
    this.reset();
    this.resetBackground();
    if(this.header) this.header.classList.remove(this.classes.hover);
  }
}

customElements.define('mega-menu', MegaMenu);
